import { useCallback, useEffect, useRef, useState } from "react";
import { database, timestamp } from "../firebase/config";

const normalizeEmailList = (value) => {
  const raw = Array.isArray(value) ? value : value ? [value] : [];
  const cleaned = raw
    .map((v) => String(v || "").trim())
    .filter(Boolean);
  return Array.from(new Set(cleaned));
};

export const useEmailNotifications = (options = {}) => {
  const defaultTo = normalizeEmailList(options.defaultTo);
  const defaultCc = normalizeEmailList(options.defaultCc);
  const defaultBcc = normalizeEmailList(options.defaultBcc);

  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [lastMailRef, setLastMailRef] = useState(null);

  const isCancelledRef = useRef(false);
  useEffect(() => {
    return () => {
      isCancelledRef.current = true;
    };
  }, []);

  const sendEmail = useCallback(
    async (params = {}) => {
      const to = normalizeEmailList(params.to);
      const cc = normalizeEmailList(params.cc);
      const bcc = normalizeEmailList(params.bcc);

      const finalTo = to.length ? to : defaultTo;
      const finalCc = cc.length ? cc : defaultCc;
      const finalBcc = bcc.length ? bcc : defaultBcc;

      const subject = String(params.subject || "").trim();
      const text = String(params.text || "").trim();
      const html = String(params.html || "").trim();

      if (finalTo.length === 0) {
        throw new Error("EMAIL_TO_REQUIRED");
      }
      if (!subject) {
        throw new Error("EMAIL_SUBJECT_REQUIRED");
      }
      if (!text && !html) {
        throw new Error("EMAIL_BODY_REQUIRED");
      }

      setIsPending(true);
      setError(null);
      setSuccess(false);
      setLastMailRef(null);

      const payload = {
        to: finalTo,
        message: {
          subject,
          ...(text ? { text } : {}),
          ...(html ? { html } : {}),
        },
        ...(finalCc.length ? { cc: finalCc } : {}),
        ...(finalBcc.length ? { bcc: finalBcc } : {}),
        ...(params.replyTo ? { replyTo: String(params.replyTo).trim() } : {}),
        ...(params.headers && typeof params.headers === "object"
          ? { headers: params.headers }
          : {}),
        ...(Array.isArray(params.attachments)
          ? { attachments: params.attachments }
          : {}),
        ...(params.data && typeof params.data === "object"
          ? { data: params.data }
          : {}),
        createdAt: timestamp.fromDate(new Date()),
      };

      try {
        const added = await database.collection("mail").add(payload);
        const ref = { id: added.id, path: added.path };

        if (!isCancelledRef.current) {
          setLastMailRef(ref);
          setSuccess(true);
          setIsPending(false);
        }

        return ref;
      } catch (e) {
        if (!isCancelledRef.current) {
          setError(e?.message || "EMAIL_SEND_FAILED");
          setIsPending(false);
          setSuccess(false);
        }
        return null;
      }
    },
    [defaultTo, defaultCc, defaultBcc],
  );

  return { sendEmail, isPending, error, success, lastMailRef };
};

